import { FirebaseApp } from 'firebase/app';
import isDomUsable from '@fenderdigital/utils/dom';

const isNotProd = process.env.REACT_APP_ENVIRONMENT !== 'prod';

export const firebaseConfig = {
  apiKey: process.env.REACT_APP_CLIENT_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_CLIENT_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_CLIENT_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_CLIENT_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_CLIENT_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_CLIENT_FIREBASE_APP_ID,
};

/**
 * Dynamically imports the Firebase App module and returns the initialized app.
 * Another reason to use a dynamic import here is to get around SSR issues,
 * since this module uses the window object.
 *
 * @return {Promise<FirebaseApp>}
 */
const getFirebaseApp = async (): Promise<FirebaseApp | null> => {
  if (!isDomUsable()) return null;
  const firebase = await import(
    /* webpackChunkName: "FirebaseApp", webpackPrefetch: true */ 'firebase/app'
  );
  const app = firebase.initializeApp(firebaseConfig);
  return app;
};

export interface CountriesFromRemoteConfig {
  approvedCountries: string[];
  nonApprovedCountries: string[];
}

/**
 * Fetches approved/non-approved countries list from the
 * remote config from the Fender Play Firebase console.
 *
 * @return {CountriesFromRemoteConfig} - it returns a object containing 2 list of approved / non approved countries.
 */
export const getCountriesFromRemoteConfig = async (): Promise<CountriesFromRemoteConfig> => {
  const app = await getFirebaseApp();
  const rc = await import(
    /* webpackChunkName: "FirebaseRemoteConfig", webpackPrefetch: true */ 'firebase/remote-config'
  );
  const remoteConfig = rc.getRemoteConfig(app as FirebaseApp);
  // default timeout value, wanted to expose it here
  // incase we need to shorten this value
  remoteConfig.settings.fetchTimeoutMillis = 60000;
  // use a shorter fetch interval for dev, see:
  // https://firebase.google.com/docs/remote-config/get-started?hl=en&platform=web#set-minimum-fetch-interval
  remoteConfig.settings.minimumFetchIntervalMillis = isNotProd ? 3600000 : 43200000;
  await rc.fetchAndActivate(remoteConfig);
  const approvedCountries = rc.getValue(remoteConfig, 'pro_approved_countries').asString();
  const nonApprovedCountries = rc.getValue(remoteConfig, 'non_approved_countries').asString();
  return {
    approvedCountries: approvedCountries.split(','),
    nonApprovedCountries: nonApprovedCountries.split(','),
  };
};
